import { Helmet } from "react-helmet"
import {
    Box,
    Divider,
    FormControlLabel,
    FormHelperText,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from "@mui/material"
import React, { useEffect, useMemo } from "react"
import { makeStyles } from "@mui/styles"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import cssVariables from "../../theme/cssVariables"
import * as Util from "../../services/global.service"
// import TimePicker from '@mui/lab/TimePicker'
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
// import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { TimePicker, LocalizationProvider, renderTimeViewClock } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'

import { ReactComponent as EmailIcon } from "../../assets/icons/email.svg"
import { ReactComponent as TimeIcon } from "../../assets/icons/time.svg"
import { ReactComponent as ClockIcon } from "../../assets/icons/clock.svg"
import { ReactComponent as AlertRed } from "../../icons/alert-red.svg"

import CustomCheckbox from "../../components/CustomCheckbox"
import RedStarRequired from "../../components/RedStarRequired"

const useStyles = makeStyles({
    plusSvg: {
        "& > path": {
            fill: "#fff",
            stroke: "#fff",
        },
    },
    paperRoot: {
        paddingTop: "24px!important",
        paddingBottom: "24px!important",
        paddingLeft: "32px!important",
        paddingRight: "32px!important",
        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)!important",
        borderRadius: "10px!important",
    },
    timePickerInput: {
        "& .MuiOutlinedInput-root > input": {
            height: "40px",
            padding: "5px 10px!important",
            fontSize: "16px!important",
            fontWeight: "400!important",
            minWidth: "400px",
            textTransform: "uppercase"
        }
    },
    timePickerInputDisabled: {
        "& .MuiOutlinedInput-root > input": {
            height: "40px",
            padding: "5px 10px!important",
            fontSize: "16px!important",
            fontWeight: "400!important",
            minWidth: "400px",
            textTransform: "uppercase",
        },
        "& .MuiOutlinedInput-root": {
            background: "#FAFAFA"
        },
        "& .MuiOutlinedInput-root > fieldset": {
            border: "0"
        },
        "& .MuiOutlinedInput-root > input.Mui-disabled": {
            "-webkit-text-fill-color": "#404349"
        }
    },
    timePickerPopper: {
        "& .MuiPaper-root": {
            width: "584px",
            boxShadow: "0px 24px 23px rgba(0, 0, 0, 0.04)"
        },
        "& .PrivatePickersToolbar-root": {
            maxWidth: "unset",
            width: "270px",
            padding: "16px 24px",
        },
        "& .PrivatePickersToolbar-root .PrivateTimePickerToolbar-separator": {
            width: "24px",
            padding: "16px 0",
            fontSize: "48px",
            fontWeight: "700",
            lineHeight: "56px",
            textAlign: "center",
            color: "rgba(0, 0, 0, 0.87)"
        },
        "& .PrivatePickersToolbar-root .MuiTypography-root": {
            // fontSize: "10px",
            // fontWeight: "400",
            // lineHeight: "16px",
            color: "rgba(0, 0, 0, 0.6)"
        },
        "& .PrivatePickersToolbar-root .PrivateTimePickerToolbar-hourMinuteLabel button .MuiTypography-root": {
            width: "96px",
            padding: "16px 0",
            fontSize: "48px",
            fontWeight: "500",
            lineHeight: "56px",
            textAlign: "center",
            background: "rgba(33, 33, 33, 0.08)",
            borderRadius: "4px",
            color: "rgba(0, 0, 0, 0.87)"
        },
        "& .PrivatePickersToolbar-root .PrivateTimePickerToolbar-hourMinuteLabel button .MuiTypography-root.Mui-selected": {
            background: "rgba(93, 161, 150, 0.15)",
            color: "#5DA196"
        },
        "& .PrivateTimePickerToolbar-ampmSelection": {
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: "4px",
        },
        "& .PrivateTimePickerToolbar-ampmSelection button:first-child": {
            borderRight: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: "0",
        },
        "& .PrivateTimePickerToolbar-ampmSelection .MuiTypography-root": {
            fontSize: "20px",
            padding: "8px 0",
            fontWeight: "500",
            lineHeight: "24px",
            textAlign: "center",
            color: "rgba(0, 0, 0, 0.87)"
        },
        "& .PrivateTimePickerToolbar-ampmSelection .MuiTypography-root.Mui-selected": {
            color: "#5DA196",
            background: "rgba(93, 161, 150, 0.15)",
            width: "100%",
        },
        "& .MuiClockPicker-arrowSwitcher": {
            visibility: "hidden"
        },
        "& div > div > div > .MuiButtonBase-root": {
            visibility: "hidden"
        },
        "& div > .PrivateTimePickerToolbar-hourMinuteLabel > button": {
            visibility: "unset !important"
        },
        "& div > .PrivateTimePickerToolbar-ampmSelection > button": {
            visibility: "unset !important",
            width: "50%",
        },
    }
})

const StyledPaper = (props) => {
    const classes = useStyles();
    return (
        <Paper
            classes={{ root: classes.paperRoot }}
            {...props}
        >
            {props.children}
        </Paper>
    )
}

const ErrorHelper = () => {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <AlertRed />
            <FormHelperText
                error={true}
                sx={{ ml: 1 }}
            >
                {t("label.pleaseFillOutThisField")}
            </FormHelperText>
        </Box>
    )
}

const StartDateForm = (props) => {
    const { doPreview } = { ...props }
    const classes = useStyles()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const notificationState = useSelector((state) => state.notificationCenter)

    const [isFormChecked, setIsFormChecked] = React.useState(false)
    const [receiverText, setReceiverText] = React.useState("")
    const [time, setTime] = React.useState(new Date())
    const [isReceiverString, setIsReceiverString] = React.useState(false)


    const handleFormChecked = (val) => {
        setIsFormChecked(val)
    }

    const handlePreview = () => {
        doPreview("start_date")
    }

    const setData = (data) => {
        if (data?.status === "active") {
            setIsFormChecked(true)
        } else if (data?.status === "inactive") {
            setIsFormChecked(false)
        }

        let tmpReceiverText = notificationState.pageCode && notificationState.pageCode !== "goal" ? "To Manager" : "To Employee"
        setReceiverText(tmpReceiverText)

        if (data?.reminder && Util.testJSON(data?.reminder)) {
            let reminder = JSON.parse(data?.reminder)
            if (reminder?.time) {
                let formatTime = new Date(`2015-03-25T${reminder.time}+07:00`)
                setTime(formatTime)
            }
        }
        console.log("")
    }

    // useEffect
    useEffect(() => {
    }, [])

    useEffect(() => {
        dispatch({
            type: "SET_DATA_STARTDATE_FORM",
            payload: {
                feature: "start_date",
                status: isFormChecked ? "active" : "inactive",
                reminder: {
                    time: time
                },
            }
        })
    }, [isFormChecked, time])

    useEffect(() => {
        if (time && time !== "") {
            dispatch({
                type: "SET_IS_STARTDATE_FORM_VALID",
                payload: true
            })
        } else {
            dispatch({
                type: "SET_IS_STARTDATE_FORM_VALID",
                payload: false
            })
        }
    }, [time])

    useEffect(() => {
        if (notificationState?.emailNotification) {
            let formData = [...notificationState.emailNotification].find(item => item.feature === "start_date")
            if (typeof formData?.receiver === 'string') {
                setIsReceiverString(true)
            } else {
                setIsReceiverString(false)
            }
            if (formData) setData(formData)
        }
    }, [notificationState.emailNotification])
    // end useEffect

    return (
        <StyledPaper>
            <Grid container>
                <Grid item xs={6} display="flex" alignItems="center">
                    <CustomCheckbox checked={isFormChecked} onChange={() => handleFormChecked(!isFormChecked)} sx={{ pl: 0 }} />
                    <Typography fontSize={20} fontWeight={600}>{t("emailNotificationCenter.label.startDateForm")}</Typography>
                </Grid>
                <Grid item xs="auto" ml="auto" display="flex" alignItems="center">
                    <Box sx={{ display: "flex", alignItems: "center" }} onClick={() => handlePreview()}>
                        <EmailIcon />
                        <Typography sx={{ ml: "10px" }} color="#5DA196" fontWeight={500} className="clickable"><u>{t("emailNotificationCenter.label.previewEmail")}</u></Typography>
                    </Box>
                </Grid>
            </Grid>
            <Grid container mt="45px">
                <Grid item md={4} xs={12} display="flex" alignItems="flex-start" flexDirection="column">
                    <Typography fontSize={18} fontWeight={500}>{t("emailNotificationCenter.label.receiver")}</Typography>
                    <Typography fontSize={14} fontWeight={400} color="#AAAAAA">{t("emailNotificationCenter.label.receiverDesc")}</Typography>
                </Grid>
                <Grid item xs="auto" display="flex" alignItems="flex-start">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {!isReceiverString ?
                            <RadioGroup
                                aria-labelledby="receive-radio-group"
                                defaultValue="1"
                                name="radio-buttons-group"
                            >
                                <FormControlLabel value="1" control={<Radio disabled={!isFormChecked} />} label={receiverText} />
                                <FormControlLabel value="employee" control={<Radio disabled />} label="To Pending Step Responsible Person" />
                            </RadioGroup>
                            : <Typography fontSize={16} fontWeight={400}>To Pending Step Responsible Person</Typography>
                        }
                    </Box>
                </Grid>
            </Grid>
            <Grid container mt="25px" mb="6px">
                <Grid item xs={12}>
                    <Divider />
                </Grid>
            </Grid>
            <Grid container mt="45px">
                <Grid item md={4} xs={12} display="flex" alignItems="flex-start" flexDirection="column">
                    <Typography fontSize={18} fontWeight={500}>{t("emailNotificationCenter.label.reminderNoti")}</Typography>
                    <Typography fontSize={14} fontWeight={400} color="#AAAAAA" sx={{ maxWidth: "300px" }}>{t("emailNotificationCenter.label.reminderNotiDesc")}</Typography>
                </Grid>
                <Grid item xs="auto" display="flex" alignItems="flex-start" flexDirection="column">
                    <Box sx={{ display: "flex", alignItems: "center", mb: "3px" }}>
                        <Typography fontSize={18} fontWeight={500}>{t("emailNotificationCenter.label.time")}</Typography>
                        <RedStarRequired />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <TimePicker
                                ampm
                                minutesStep={5}
                                slots={{
                                    OpenPickerIcon: ClockIcon
                                }}
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                showToolbar={true}
                                orientation="landscape"
                                value={time}
                                onChange={(val) => setTime(val)}
                                inputProps={{ placeholder: t('admin.inputForm.placeholder.selectTime'), readOnly: true }}
                                PopperProps={{
                                    className: classes.timePickerPopper
                                }}
                                disabled={!isFormChecked}
                                renderInput={(params) => <TextField {...params} className={isFormChecked ? classes.timePickerInput : classes.timePickerInputDisabled} error={notificationState.doValidateNotification && (!time || time == "")} />}
                            />
                        </LocalizationProvider>
                    </Box>
                    {notificationState.doValidateNotification && (!time || time == "") && <ErrorHelper />}
                </Grid>
            </Grid>
        </StyledPaper>
    )
}
export default StartDateForm
